"use client";

import type { OmniboxDemo, Voice as PayloadVoice } from "@mw/shared/payload-types";
import type { Voice } from "@/app/_data/voices";

import { useState, useMemo, useCallback, useEffect } from "react";
import clsx from "clsx";

import { transformPayloadVoice } from "@/app/_utilities/payload";

import { BookCoverCard } from "./BookCoverCard";
import { TranscriptionCard } from "./TranscriptionCard";

/** Audiobook sample type derived from CMS schema */
export type AudioBookSample = NonNullable<OmniboxDemo["audiobookSamples"]>[number];

export interface AudioBookProps {
  /** Array of audiobook samples to display */
  audiobookSamples?: AudioBookSample[] | null;
  /** Available voices for TTS generation */
  audiobookVoices?: (string | PayloadVoice)[] | null;
}

export function AudioBook({ audiobookSamples, audiobookVoices }: AudioBookProps) {
  const [currentBookIndex, setCurrentBookIndex] = useState(0);

  const samples = audiobookSamples ?? [];
  const currentSample = samples[currentBookIndex];

  // Transform payload voices to the Voice type used by VoiceSelector
  const voices = useMemo(() => {
    if (!audiobookVoices) return [];
    return audiobookVoices.filter((v): v is PayloadVoice => typeof v !== "string").map(transformPayloadVoice);
  }, [audiobookVoices]);

  const [selectedVoice, setSelectedVoice] = useState<Voice | undefined>(() => voices[0]);

  // Update selected voice when voices array changes (e.g., on initial load or hydration)
  useEffect(() => {
    if (voices.length > 0 && !selectedVoice) {
      setSelectedVoice(voices[0]);
    }
  }, [voices, selectedVoice]);

  const handlePrev = useCallback(() => {
    setCurrentBookIndex(prev => (prev > 0 ? prev - 1 : samples.length - 1));
  }, [samples.length]);

  const handleNext = useCallback(() => {
    setCurrentBookIndex(prev => (prev < samples.length - 1 ? prev + 1 : 0));
  }, [samples.length]);

  // Early return if no samples
  if (!currentSample) {
    return null;
  }

  const {
    coverImage: displayCoverImage,
    backgroundImage: displayBackgroundImage,
    title: displayTitle,
    subtitle: displaySubtitle,
    author: displayAuthor,
    text: displayText,
    chapterLabel: displayChapterLabel,
    chapterTitle: displayChapterTitle,
  } = currentSample;

  return (
    <div className={clsx("tw-flex tw-flex-col lg:tw-flex-row tw-gap-2.5 tw-h-[21.25rem] md:tw-h-[36rem] tw-w-full")}>
      {/* Left card - Book cover */}
      <div className='tw-flex-1 tw-min-w-0 tw-h-full tw-hidden lg:tw-block'>
        <BookCoverCard
          coverImage={displayCoverImage}
          backgroundImage={displayBackgroundImage}
          title={displayTitle}
          subtitle={displaySubtitle}
          author={displayAuthor}
          onPrev={samples.length > 1 ? handlePrev : undefined}
          onNext={samples.length > 1 ? handleNext : undefined}
        />
      </div>

      {/* Right card - Transcription with TTS */}
      <div className='tw-flex-1 tw-min-w-0 tw-h-full'>
        <TranscriptionCard
          text={displayText}
          chapterLabel={displayChapterLabel}
          chapterTitle={displayChapterTitle}
          bookTitle={displayTitle}
          author={displayAuthor}
          voices={voices}
          selectedVoice={selectedVoice}
          onVoiceChange={setSelectedVoice}
          onPrev={samples.length > 1 ? handlePrev : undefined}
          onNext={samples.length > 1 ? handleNext : undefined}
        />
      </div>
    </div>
  );
}

export default AudioBook;
